import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, getDocs, getDoc, doc, query, where,
    Timestamp, updateDoc, onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
    Box, Typography, CircularProgress, Divider,
    IconButton, Button, Switch, TextField,
} from '@mui/material';
import {
    ContentCut, People, CalendarToday, QrCode2,
    Logout, CheckCircle, AccessTime,
    PersonOutline, Home, BarChart, Edit, Save,
    TrendingUp, Star, Close, ArrowBack,
    Download, Fullscreen,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import InviteBarber from './InviteBarber';
import { colors, typography, statusColors, glassCard } from '../../theme';
import ShopStats from './ShopStats';
import RevenueScreen from './RevenuePage';
import EditShopPage from './EditShopPage';

const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

// ─── Plan feature gates ───────────────────────────────────────────────────────
const PLAN_LIMITS  = { free: 1, basic: 3, pro: 999, master: 999 };
const PLAN_LABELS  = { free: 'Free', basic: 'Basic (RM49/mo)', pro: 'Pro (RM99/mo)', master: 'Master (RM150/mo)' };

const canAccessFeature = (shop, feature) => {
    const plan = shop?.plan ?? 'free';
    if (feature === 'stats')   return ['pro', 'master'].includes(plan);
    if (feature === 'revenue') return ['pro', 'master'].includes(plan);
    return true;
};

// ─── Upgrade Prompt ───────────────────────────────────────────────────────────
const UpgradePrompt = ({ feature, shop }) => {
    const plan     = shop?.plan ?? 'free';
    const required = 'Pro';
    return (
        <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
            <Typography sx={{ fontSize: 40, mb: 2 }}>🔒</Typography>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: colors.textPrimary, mb: 1 }}>
                {feature === 'stats' ? 'Analytics' : 'Revenue'} is a {required} feature
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 0.5 }}>You're currently on the</Typography>
            <Box sx={{ display: 'inline-flex', px: 1.5, py: 0.5, bgcolor: colors.bgCard, border: '1px solid #c8a97e44', borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 13, color: colors.primary, fontWeight: 700 }}>{PLAN_LABELS[plan] ?? 'Free'} plan</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 3 }}>
                Upgrade to <Typography component="span" sx={{ color: colors.primary, fontWeight: 700 }}>Pro (RM99/mo)</Typography> or higher to unlock {feature === 'stats' ? 'analytics' : 'revenue tracking'}.
            </Typography>
            <Box sx={{ bgcolor: colors.bgCard, border: '1px solid #c8a97e44', borderRadius: 2, p: 2, display: 'inline-block' }}>
                <Typography sx={{ fontSize: 13, color: colors.primary, fontWeight: 600 }}>📱 Contact DigitalQ to upgrade</Typography>
            </Box>
        </Box>
    );
};

function formatTime(minutes) {
    if (!minutes && minutes !== 0) return '—';
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`;
}

const isSubscriptionActive = (shop) => {
    const plan = shop?.plan ?? 'free';
    if (plan === 'free') return true;
    const paidUntil = shop?.paidUntil?.toDate?.() ?? (shop?.paidUntil ? new Date(shop.paidUntil) : null);
    if (!paidUntil) return false;
    return paidUntil > new Date();
};

const getDaysUntilExpiry = (shop) => {
    const paidUntil = shop?.paidUntil?.toDate?.() ?? (shop?.paidUntil ? new Date(shop.paidUntil) : null);
    if (!paidUntil) return null;
    return Math.ceil((paidUntil.getTime() - Date.now()) / 86400000);
};

function timeAgo(isoString) {
    if (!isoString) return '';
    const diff  = Date.now() - new Date(isoString).getTime();
    const mins  = Math.floor(diff / 60000), hours = Math.floor(mins / 60), days = Math.floor(hours / 24);
    if (days > 0)  return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0)  return `${mins}m ago`;
    return 'just now';
}

const STATUS_COLOR = {
    waiting:   { bg: colors.warningBg,  border: colors.warningBorder,  text: colors.primary },
    serving:   { bg: colors.successBg,  border: colors.successBorder,  text: colors.success },
    completed: { bg: colors.infoBg,     border: colors.infoBorder,     text: colors.info },
    cancelled: { bg: colors.errorBg,    border: colors.errorBorder,    text: colors.error },
    pending:   { bg: colors.warningBg,  border: colors.warningBorder,  text: colors.primary },
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: colors.bgDeep, borderRadius: 2, color: colors.textPrimary, fontSize: 13,
        '& fieldset': { borderColor: colors.border },
        '&:hover fieldset': { borderColor: colors.primaryBorder },
        '&.Mui-focused fieldset': { borderColor: colors.primary },
    },
    '& input': { color: colors.textPrimary },
    '& .MuiInputLabel-root': { color: colors.textMuted, fontSize: 13 },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.primary },
};

const Stars = ({ rating, size = 13 }) => (
    <Box sx={{ display: 'flex', gap: 0.2 }}>
        {[1,2,3,4,5].map(s => (
            <Star key={s} sx={{ fontSize: size, color: s <= Math.round(rating ?? 0) ? colors.primary : colors.border }} />
        ))}
    </Box>
);

const StatCard = ({ icon, label, value, sub, color = colors.primary }) => (
    <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5, flex: 1, minWidth: 0, ...glassCard }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            {icon}
            <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{label}</Typography>
        </Box>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 0.3 }}>{sub}</Typography>}
    </Box>
);

const BookingRow = ({ booking, onAction }) => {
    const s        = STATUS_COLOR[booking.status] ?? STATUS_COLOR.pending;
    const isWalkin = booking.type === 'walkin';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5, gap: 1.5, ...glassCard }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: colors.bg, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {isWalkin ? <PersonOutline sx={{ fontSize: 18, color: colors.textMuted }} /> : <ContentCut sx={{ fontSize: 16, color: colors.primary }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }} noWrap>{booking.customerName ?? 'Customer'}</Typography>
                    {isWalkin && (
                        <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                            <Typography sx={{ fontSize: 9, color: colors.textMuted }}>WALK-IN</Typography>
                        </Box>
                    )}
                </Box>
                <Typography sx={{ fontSize: 11, color: colors.textFaint }}>
                    {booking.service ?? 'Service TBD'} · {formatTime(booking.slotMinutes)}{booking.queueNumber ? ` · #${booking.queueNumber}` : ''}
                </Typography>
            </Box>
            <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: s.bg, border: `1px solid ${s.border}`, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 10, color: s.text, fontWeight: 600 }}>{booking.status?.toUpperCase()}</Typography>
            </Box>
            {booking.status === 'waiting' && (
                <Button size="small" onClick={() => onAction(booking, 'serving')}
                    sx={{ minWidth: 0, px: 1.2, py: 0.3, fontSize: 11, bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success, borderRadius: 1, textTransform: 'none', '&:hover': { bgcolor: colors.successBorder }, flexShrink: 0 }}>
                    Call
                </Button>
            )}
            {booking.status === 'serving' && (
                <Button size="small" onClick={() => onAction(booking, 'completed')}
                    sx={{ minWidth: 0, px: 1.2, py: 0.3, fontSize: 11, bgcolor: colors.infoBg, border: `1px solid ${colors.infoBorder}`, color: colors.info, borderRadius: 1, textTransform: 'none', '&:hover': { bgcolor: colors.infoBorder }, flexShrink: 0 }}>
                    Done
                </Button>
            )}
        </Box>
    );
};

// ─── QR Panel ─────────────────────────────────────────────────────────────────
const QRPanel = ({ walkinUrl, shopName }) => {
    const svgRef       = useRef(null);
    const [expanded, setExpanded] = useState(false);

    // Download QR as PNG
    const handleDownload = () => {
        const svg    = svgRef.current?.querySelector('svg');
        if (!svg) return;

        const size   = 512;
        const canvas = document.createElement('canvas');
        canvas.width  = size;
        canvas.height = size;
        const ctx    = canvas.getContext('2d');

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        // Serialise SVG → data URL
        const svgData   = new XMLSerializer().serializeToString(svg);
        const svgBlob   = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl    = URL.createObjectURL(svgBlob);
        const img       = new Image();
        img.onload = () => {
            ctx.drawImage(img, 0, 0, size, size);
            URL.revokeObjectURL(svgUrl);
            const a       = document.createElement('a');
            a.download    = `DigitalQ_QR_${(shopName ?? 'shop').replace(/\s+/g, '_')}.png`;
            a.href        = canvas.toDataURL('image/png');
            a.click();
        };
        img.src = svgUrl;
    };

    return (
        <>
            {/* Fullscreen overlay */}
            {expanded && (
                <Box sx={{
                    position: 'fixed', inset: 0, zIndex: 300,
                    bgcolor: 'rgba(0,0,0,0.92)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    px: 4,
                }}>
                    <IconButton onClick={() => setExpanded(false)}
                        sx={{ position: 'absolute', top: 20, right: 20, color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }}>
                        <Close />
                    </IconButton>

                    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: colors.primary, mb: 3, textAlign: 'center' }}>
                        {shopName ?? 'Walk-in QR'}
                    </Typography>

                    {/* Big QR */}
                    <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 3 }} ref={svgRef}>
                        <QRCodeSVG value={walkinUrl} size={280} />
                    </Box>

                    <Typography sx={{ fontSize: 13, color:colors.bg, mt: 2.5, textAlign: 'center' }}>
                        Customers scan this to join the walk-in queue
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: colors.bg, mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center', maxWidth: 320 }}>
                        {walkinUrl}
                    </Typography>

                    {/* Download button in fullscreen */}
                    <Button
                        startIcon={<Download sx={{ fontSize: 16 }} />}
                        onClick={handleDownload}
                        sx={{
                            mt: 3, px: 3, py: 1,
                            bgcolor: colors.primary, color: '#322920',
                            fontWeight: 700, fontSize: 14, textTransform: 'none',
                            borderRadius: 2,
                            '&:hover': { bgcolor: colors.primary, opacity: 0.9 },
                        }}
                    >
                        Download QR as PNG
                    </Button>
                </Box>
            )}

            {/* Inline panel */}
            <Box sx={{ mx: 2, mt: 2, p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.primaryBorder}`, borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 600, color: colors.primary }}>
                        Walk-in QR Code
                    </Typography>
                    {/* action buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={handleDownload}
                            title="Download as PNG"
                            sx={{ color: colors.primary, bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, borderRadius: 1.5, p: 0.6 }}>
                            <Download sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(true)}
                            title="Fullscreen"
                            sx={{ color: colors.primary, bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, borderRadius: 1.5, p: 0.6 }}>
                            <Fullscreen sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* QR code — ref here for inline download */}
                <Box sx={{ p: 1.5, bgcolor: colors.white, borderRadius: 2 }} ref={!expanded ? svgRef : undefined}>
                    <QRCodeSVG value={walkinUrl} size={160} />
                </Box>

                <Typography sx={{ fontSize: 11, color: colors.textPrimary, textAlign: 'center', maxWidth: 240 }}>
                    Customers scan this to join the walk-in queue
                </Typography>
                <Typography sx={{ fontSize: 10, color: colors.textFaint, fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
                    {walkinUrl}
                </Typography>
            </Box>
        </>
    );
};

// ─── Barber Profile Drawer ────────────────────────────────────────────────────
const BarberProfileDrawer = ({ barber, bookings, onClose }) => {
    const [allBookings, setAllBookings] = useState([]);
    const [loadingAll,  setLoadingAll]  = useState(true);

    useEffect(() => {
        if (!barber) return;
        setLoadingAll(true);
        const load = async () => {
            try {
                const snap = await getDocs(query(
                    collection(db, 'bookings'),
                    where('barberId', '==', barber.id),
                    where('status',   '==', 'completed'),
                ));
                setAllBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { console.error(e); }
            setLoadingAll(false);
        };
        load();
    }, [barber?.id]);

    if (!barber) return null;

    const todayBookings  = bookings.filter(b => b.barberId === barber.id);
    const serving        = todayBookings.find(b => b.status === 'serving');
    const waitingCount   = todayBookings.filter(b => b.status === 'waiting' || b.status === 'pending').length;
    const completedToday = todayBookings.filter(b => b.status === 'completed').length;
    const reviews        = (barber.reviews ?? []).filter(r => r.rating > 0 || r.review).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const rating         = barber.rating      ?? barber.ratings?.average      ?? 0;
    const ratingCount    = barber.ratingCount ?? barber.ratings?.totalReviews ?? 0;
    const totalCompleted = allBookings.length;
    const totalRevenue   = allBookings.reduce((sum, b) => {
        if (b.totalPrice != null) return sum + b.totalPrice;
        if (b.servicePrice != null) return sum + (b.servicePrice ?? 0) + (b.surcharge ?? 0);
        return sum + (b.price ?? 0);
    }, 0);
    const specificRequests = allBookings.filter(b => b.barberMode === 'specific').length;
    const specificPct      = totalCompleted > 0 ? Math.round((specificRequests / totalCompleted) * 100) : 0;
    const serviceMap = {};
    allBookings.forEach(b => {
        const labels = b.serviceLabels ?? (b.serviceLabel ? [b.serviceLabel] : null) ?? (b.service ? [b.service] : []);
        labels.forEach(svc => { if (!svc) return; serviceMap[svc] = (serviceMap[svc] ?? 0) + 1; });
    });
    const serviceBreakdown = Object.entries(serviceMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    const now = new Date(), monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth = allBookings.filter(b => b.scheduledAt?.toDate?.() >= monthStart).length;

    return (
        <>
            <Box onClick={onClose} sx={{ position: 'fixed', inset: 0, zIndex: 200, bgcolor: 'rgba(0,0,0,0.6)' }} />
            <Box sx={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 201,
                bgcolor: colors.bg, borderRadius: '20px 20px 0 0',
                border: `1px solid ${colors.border}`, borderBottom: 'none',
                maxHeight: '88vh', overflowY: 'auto',
                animation: 'slideUp .25s ease',
                '@keyframes slideUp': { from: { transform: 'translateY(100%)' }, to: { transform: 'translateY(0)' } },
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
                    <Box sx={{ width: 36, height: 4, bgcolor: colors.border, borderRadius: 2 }} />
                </Box>
                <Box sx={{ px: 3, pt: 1, pb: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box component="img" src={barber.image || '/barber-fallback.png'} alt={barber.name}
                        sx={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${colors.primary}44`, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: colors.textPrimary }}>{barber.name}</Typography>
                        {barber.specialty?.length > 0 && <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 0.2 }}>{barber.specialty.join(' · ')}</Typography>}
                        {rating > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5 }}>
                                <Stars rating={rating} size={12} />
                                <Typography sx={{ fontSize: 11, color: colors.textMuted }}>{rating.toFixed(1)} ({ratingCount} review{ratingCount !== 1 ? 's' : ''})</Typography>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: barber.isAvailable !== false ? colors.success : colors.error }} />
                        <Typography sx={{ fontSize: 9, color: colors.textFaintest }}>{barber.isAvailable !== false ? 'Available' : 'Off'}</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: colors.textFaintest }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                <Divider sx={{ borderColor: colors.border, mx: 3 }} />

                <Box sx={{ px: 3, py: 2 }}>
                    <Typography sx={{ fontSize: 11, color: colors.textFaintest, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Today</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {[{ label: 'Waiting', value: waitingCount, color: colors.primary }, { label: 'Serving', value: serving ? 1 : 0, color: colors.success }, { label: 'Done', value: completedToday, color: colors.info }].map(s => (
                            <Box key={s.label} sx={{ flex: 1, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                                <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 0.3 }}>{s.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {serving && (
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Box sx={{ bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.success, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontSize: 12, color: colors.success, fontWeight: 600 }}>Now Serving</Typography>
                                <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: 700 }}>{serving.customerName ?? 'Customer'}</Typography>
                                <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{serving.service ?? 'Service TBD'} · {formatTime(serving.slotMinutes)}</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                <Divider sx={{ borderColor: colors.border, mx: 3 }} />

                <Box sx={{ px: 3, py: 2 }}>
                    <Typography sx={{ fontSize: 11, color: colors.textFaintest, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>All Time</Typography>
                    {loadingAll ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={18} sx={{ color: colors.primary }} /></Box>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                {[{ label: 'Total Cuts', value: totalCompleted, color: colors.primary, prefix: '' }, { label: 'This Month', value: thisMonth, color: colors.primary, prefix: '' }, { label: 'Total Revenue', value: totalRevenue.toFixed(0), color: colors.success, prefix: 'RM' }].map(s => (
                                    <Box key={s.label} sx={{ flex: 1, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
                                        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.prefix}{s.value}</Typography>
                                        <Typography sx={{ fontSize: 9, color: colors.textFaintest, mt: 0.3 }}>{s.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            {totalCompleted > 0 && (
                                <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                        <Typography sx={{ fontSize: 12, color: colors.textMuted }}>⭐ Specifically requested</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.primary }}>{specificRequests}x <Typography component="span" sx={{ fontSize: 10, color: colors.textFaintest }}>({specificPct}%)</Typography></Typography>
                                    </Box>
                                    <Box sx={{ height: 5, bgcolor: colors.bg, borderRadius: 1, overflow: 'hidden' }}>
                                        <Box sx={{ height: '100%', bgcolor: colors.primary, borderRadius: 1, width: `${specificPct}%`, transition: 'width .6s ease' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 10, color: colors.textDisabled, mt: 0.5 }}>of all their customers chose this barber specifically (+RM5 each)</Typography>
                                </Box>
                            )}
                            {serviceBreakdown.length > 0 && (
                                <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5 }}>
                                    <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 1.2 }}>✂️ Haircut types requested</Typography>
                                    {serviceBreakdown.map((svc, i) => {
                                        const pct = totalCompleted > 0 ? (svc.count / totalCompleted) * 100 : 0;
                                        return (
                                            <Box key={svc.name} sx={{ mb: i < serviceBreakdown.length - 1 ? 1.5 : 0 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                                                    <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>{svc.name}</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                        <Typography sx={{ fontSize: 11, color: colors.textMuted }}>{svc.count}x</Typography>
                                                        <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>({pct.toFixed(0)}%)</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ height: 4, bgcolor: colors.bg, borderRadius: 1, overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', borderRadius: 1, background: `linear-gradient(90deg, ${colors.primary}, ${colors.primary}88)`, width: `${pct}%`, transition: 'width .6s ease' }} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                            {totalCompleted === 0 && <Box sx={{ textAlign: 'center', py: 2 }}><Typography sx={{ fontSize: 13, color: colors.textFaintest }}>No completed bookings yet</Typography></Box>}
                        </>
                    )}
                </Box>

                <Divider sx={{ borderColor: colors.primary, mx: 3 }} />

                <Box sx={{ px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 700, color: colors.primary }}>Reviews</Typography>
                        {ratingCount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Star sx={{ fontSize: 13, color: colors.primary }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.primary }}>{rating.toFixed(1)}</Typography>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest }}>/ 5</Typography>
                            </Box>
                        )}
                    </Box>
                    {reviews.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography sx={{ fontSize: 24, mb: 1 }}>⭐</Typography>
                            <Typography sx={{ fontSize: 13, color: colors.textFaintest }}>No reviews yet</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {reviews.map((r, i) => (
                                <Box key={r.bookingId ?? i} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: colors.bg, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography sx={{ fontSize: 12 }}>👤</Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>{r.customerName ?? 'Walk-in'}</Typography>
                                                <Typography sx={{ fontSize: 10, color: colors.textDisabled }}>{timeAgo(r.createdAt)}</Typography>
                                            </Box>
                                        </Box>
                                        {r.rating > 0 && <Stars rating={r.rating} size={11} />}
                                    </Box>
                                    {r.review && <Typography sx={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.6, fontStyle: 'italic' }}>"{r.review}"</Typography>}
                                    {r.services?.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.8 }}>
                                            {r.services.map((svc, j) => (
                                                <Box key={j} sx={{ px: 0.8, py: 0.2, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                                                    <Typography sx={{ fontSize: 9, color: colors.primary }}>✂️ {svc}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                <Box sx={{ height: 32 }} />
            </Box>
        </>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ShopDashboard = () => {
    const { shopId } = useParams();
    const navigate   = useNavigate();

    const [shop,           setShop]           = useState(null);
    const [barbers,        setBarbers]        = useState([]);
    const [bookings,       setBookings]       = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [authReady,      setAuthReady]      = useState(false);
    const [activeTab,      setActiveTab]      = useState('main');
    const [innerTab,       setInnerTab]       = useState('queue');
    const [showQR,         setShowQR]         = useState(false);
    const [selectedBarber, setSelectedBarber] = useState(null);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { if (!user) navigate('/signin'); setAuthReady(true); });
        return () => unsub();
    }, [navigate]);

    const loadShop = async () => {
        try {
            const [shopSnap, barbersSnap] = await Promise.all([getDoc(doc(db,'shops',shopId)), getDocs(collection(db,'shops',shopId,'barbers'))]);
            if (shopSnap.exists()) setShop({ id: shopSnap.id, ...shopSnap.data() });
            setBarbers(barbersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (!authReady) return; loadShop(); }, [authReady, shopId]);

    useEffect(() => {
        if (!authReady) return;
        const today = new Date(); today.setHours(0,0,0,0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const q = query(collection(db,'bookings'), where('shopId','==',shopId), where('scheduledAt','>=',Timestamp.fromDate(today)), where('scheduledAt','<',Timestamp.fromDate(tomorrow)));
        const unsub = onSnapshot(q, (snap) => {
            setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => (a.slotMinutes??0)-(b.slotMinutes??0)));
        }, (err) => console.error('Bookings listener:', err));
        return () => unsub();
    }, [authReady, shopId]);

    const handleAction = async (booking, newStatus) => {
        try { await updateDoc(doc(db,'bookings',booking.id), { status: newStatus }); }
        catch (e) { console.error(e); }
    };

    const walkins      = bookings.filter(b => b.type === 'walkin');
    const appointments = bookings.filter(b => b.type === 'appointment');
    const waiting      = bookings.filter(b => b.status === 'waiting').length;
    const serving      = bookings.filter(b => b.status === 'serving').length;
    const completed    = bookings.filter(b => b.status === 'completed').length;

    const todayDayKey = DAY_KEYS[new Date().getDay()];
    const todayHours  = shop?.operatingHours?.[todayDayKey];
    const isOpen      = todayHours && !todayHours.isOff;
    const walkinUrl   = `${window.location.origin}/walkin/${shopId}`;

    if (!authReady || loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    const NAV = [
        { key: 'main',    label: 'Main',    icon: <Home sx={{ fontSize: 20 }} /> },
        { key: 'stats',   label: 'Stats',   icon: <BarChart sx={{ fontSize: 20 }} />, locked: !canAccessFeature(shop, 'stats') },
        { key: 'revenue', label: 'Revenue', icon: <TrendingUp sx={{ fontSize: 20 }} />, locked: !canAccessFeature(shop, 'revenue') },
        { key: 'edit',    label: 'Edit',    icon: <Edit sx={{ fontSize: 20 }} /> },
    ];

    const INNER_TABS = [
        { key: 'queue',        label: `Queue (${walkins.length})` },
        { key: 'appointments', label: `Appts (${appointments.length})` },
        { key: 'barbers',      label: `Team (${barbers.length})` },
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, color: colors.textPrimary, pb: 10 }}>

            <BarberProfileDrawer barber={selectedBarber} bookings={bookings} onClose={() => setSelectedBarber(null)} />

            {/* Top Bar */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 100, bgcolor: colors.bgTopBar, borderBottom: `1px solid ${colors.borderLight}`, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 }}>
                        {shop?.shopName ?? 'Dashboard'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isOpen ? colors.success : colors.error }} />
                        <Typography sx={{ fontSize: 11, color: isOpen ? colors.success : colors.error }}>
                            {isOpen ? `Open · ${todayHours?.open} – ${todayHours?.close}` : 'Closed today'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" onClick={() => setShowQR(s => !s)}
                        sx={{ color: colors.primary, bgcolor: colors.bgCard, border: `1px solid ${colors.border}` }}>
                        <QrCode2 fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => signOut(auth).then(() => navigate('/signin'))}
                        sx={{ color: colors.error, bgcolor: colors.bgCard, border: `1px solid ${colors.border}` }}>
                        <Logout fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Subscription banner */}
            {(() => {
                const active = isSubscriptionActive(shop), daysLeft = getDaysUntilExpiry(shop), plan = shop?.plan ?? 'free';
                if (plan === 'free' || (active && daysLeft > 7)) return null;
                if (!active) return (
                    <Box sx={{ mx: 2, mt: 2, px: 2, py: 1.5, bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontSize: 20, flexShrink: 0 }}>🔒</Typography>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.error }}>Subscription Expired</Typography>
                            <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.2 }}>Your shop is hidden from customers. Contact DigitalQ to renew your plan.</Typography>
                        </Box>
                    </Box>
                );
                return (
                    <Box sx={{ mx: 2, mt: 2, px: 2, py: 1.5, bgcolor: colors.warningBg, border: `1px solid ${colors.warningBorder}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontSize: 20, flexShrink: 0 }}>⚠️</Typography>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.warning }}>Subscription expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</Typography>
                            <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.2 }}>Contact DigitalQ to renew and keep your shop visible to customers.</Typography>
                        </Box>
                    </Box>
                );
            })()}

            {/* QR Panel */}
            {showQR && <QRPanel walkinUrl={walkinUrl} shopName={shop?.shopName} />}

            {/* ════ MAIN TAB ════ */}
            {activeTab === 'main' && (
                <>
                    <Box sx={{ px: 2, mt: 2, display: 'flex', gap: 1.5 }}>
                        <StatCard icon={<AccessTime sx={{ fontSize: 13, color: colors.primary }} />} label="Waiting" value={waiting}   color={colors.primary} />
                        <StatCard icon={<ContentCut sx={{ fontSize: 13, color: colors.info }} />}    label="Serving" value={serving}   color={colors.info} />
                        <StatCard icon={<CheckCircle sx={{ fontSize: 13, color: colors.success }} />} label="Done"  value={completed} color={colors.success} />
                    </Box>

                    <Box sx={{ display: 'flex', px: 2, mt: 2, gap: 1 }}>
                        {INNER_TABS.map(tab => (
                            <Box key={tab.key} onClick={() => setInnerTab(tab.key)} sx={{ px: 1.5, py: 0.8, borderRadius: 2, cursor: 'pointer', flex: 1, textAlign: 'center', bgcolor: innerTab === tab.key ? colors.primary : colors.bgCard, border: `1px solid ${innerTab === tab.key ? colors.primary : colors.border}`, transition: 'all .2s' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: innerTab === tab.key ? colors.textPrimary : colors.textPrimary }}>{tab.label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ borderColor: colors.border, mx: 2, mt: 2 }} />

                    {innerTab === 'queue' && (
                        <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 600, color: colors.primary }}>Today's Queue</Typography>
                            {walkins.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ fontSize: 28 }}>✂️</Typography>
                                    <Typography sx={{ fontSize: 14, color: colors.textFaint, mt: 1 }}>No walk-ins yet</Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>Share the QR code ↑</Typography>
                                </Box>
                            ) : walkins.sort((a,b) => (a.queueNumber??0)-(b.queueNumber??0)).map(b => <BookingRow key={b.id} booking={b} onAction={handleAction} />)}
                        </Box>
                    )}

                    {innerTab === 'appointments' && (
                        <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 600, color: colors.primary }}>Today's Appointments</Typography>
                            {appointments.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ fontSize: 28 }}>📅</Typography>
                                    <Typography sx={{ fontSize: 14, color: colors.textFaint, mt: 1 }}>No appointments today</Typography>
                                </Box>
                            ) : appointments.map(b => <BookingRow key={b.id} booking={b} onAction={handleAction} />)}
                        </Box>
                    )}

                    {innerTab === 'barbers' && (
                        <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 600, color: colors.primary }}>Your Team</Typography>
                            <Typography sx={{ fontSize: 11, color: colors.textFaintest, mt: -1 }}>Tap a barber to view their profile & reviews</Typography>
                            {barbers.map(barber => {
                                const bb = bookings.filter(b => b.barberId === barber.id);
                                const bServe = bb.find(b => b.status === 'serving');
                                const bWait  = bb.filter(b => b.status === 'waiting').length;
                                const bRating = barber.rating ?? barber.ratings?.average ?? 0;
                                const bCount  = barber.ratingCount ?? barber.ratings?.totalReviews ?? 0;
                                return (
                                    <Box key={barber.id} onClick={() => setSelectedBarber(barber)}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5, cursor: 'pointer', transition: 'all .15s', '&:hover': { borderColor: colors.primaryBorder }, ...glassCard }}>
                                        <Box component="img" src={barber.image || '/barber-fallback.png'} alt={barber.name}
                                            sx={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{barber.name}</Typography>
                                            <Typography sx={{ fontSize: 11, color: colors.textFaint }}>
                                                {bServe ? `✂️ Serving · ${bServe.customerName ?? 'Customer'}` : `${bWait} waiting`}
                                            </Typography>
                                            {bRating > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                    <Stars rating={bRating} size={10} />
                                                    <Typography sx={{ fontSize: 10, color: colors.textMuted }}>{bRating.toFixed(1)} ({bCount})</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: barber.isAvailable !== false ? colors.success : colors.error }} />
                                            <Typography sx={{ fontSize: 11, color: colors.textFaintest }}>›</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                            <InviteBarber shopId={shopId} shopName={shop?.shopName} shop={shop} currentBarberCount={barbers.length} />
                        </Box>
                    )}
                </>
            )}

            {activeTab === 'revenue' && (canAccessFeature(shop, 'revenue') ? <RevenueScreen shopId={shopId} barbers={barbers} /> : <UpgradePrompt feature="revenue" shop={shop} />)}
            {activeTab === 'stats'   && (canAccessFeature(shop, 'stats')   ? <ShopStats shopId={shopId} shopName={shop?.shopName} barbers={barbers} /> : <UpgradePrompt feature="stats" shop={shop} />)}
            {activeTab === 'edit'    && <EditShopPage shop={shop} shopId={shopId} onSaved={loadShop} />}

            {/* Bottom nav */}
            <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: colors.bgTopBar, borderTop: '1px solid #3e2c1c', px: 3, py: 1.5, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                {NAV.map(item => (
                    <Box key={item.key} onClick={() => setActiveTab(item.key)}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, cursor: 'pointer', transition: 'opacity .2s', position: 'relative' }}>
                        <Box sx={{ color: activeTab === item.key ? colors.primary : colors.textPrimary }}>{item.icon}</Box>
                        <Typography sx={{ fontSize: 10, color: activeTab === item.key ? colors.primary : colors.textPrimary }}>{item.label}</Typography>
                        {item.locked && <Box sx={{ position: 'absolute', top: -2, right: -2, fontSize: 8 }}>🔒</Box>}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default ShopDashboard;